import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Generic interface for server filtering parameters
 */
export interface ServerFilterParams {
  [key: string]: any;
  page_size?: number;
  page_offset?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

/**
 * Generic interface for server filtering response
 */
export interface ServerFilterResponse<T> {
  data: T[];
  total_count: number;
  page_info: {
    page: number;
    page_size: number;
    total_pages: number;
    total_records: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

/**
 * Configuration for useServerFiltering hook
 */
export interface UseServerFilteringConfig<T> {
  /** Name of the RPC function to call */
  rpcFunction: string;
  /** Default filter parameters */
  defaultFilters: ServerFilterParams;
  /** Debounce delay for search inputs in milliseconds */
  debounceDelay?: number;
  /** Transform function for response data */
  transformData?: (data: any[]) => T[];
  /** Force a refetch every time the hook mounts (default: TanStack default — refetch only if stale) */
  refetchOnMount?: boolean | 'always';
}

/**
 * Return type for useServerFiltering hook
 */
export interface UseServerFilteringReturn<T> {
  /** Filtered data results */
  data: T[];
  /** Current pagination information */
  pagination: ServerFilterResponse<T>['page_info'] | null;
  /** Summary data for filtered results (if supported by RPC function) */
  summary: any;
  /** Initial loading state (only true on first load) */
  loading: boolean;
  /** Filtering/updating state (true during filter changes) */
  filtering: boolean;
  /** Error state */
  error: string | null;
  /** Current filter values */
  filters: ServerFilterParams;
  /** Update filter values */
  setFilters: (filters: Partial<ServerFilterParams>) => void;
  /** Refetch data with current filters */
  refetch: () => Promise<void>;
  /** Reset filters to defaults */
  resetFilters: () => void;
}

/**
 * Custom hook for debouncing values
 */
export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Prepare filters for RPC call (convert empty arrays/strings to null)
 */
const prepareFiltersForRPC = (filterParams: ServerFilterParams): Record<string, any> => {
  return Object.entries(filterParams).reduce((acc, [key, value]) => {
    if (Array.isArray(value) && value.length === 0) {
      acc[key] = null;
    } else if (value === '') {
      acc[key] = null;
    } else {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, any>);
};

/**
 * Custom hook for server-side filtering with pagination
 * Now powered by TanStack Query for automatic caching
 * 
 * @param config Configuration object for the hook
 * @returns Object containing data, pagination, loading state, and filter management functions
 */
export const useServerFiltering = <T>({
  rpcFunction,
  defaultFilters,
  debounceDelay = 300,
  transformData,
  refetchOnMount,
}: UseServerFilteringConfig<T>): UseServerFilteringReturn<T> => {
  const supabase = useSupabaseClient();
  const queryClient = useQueryClient();
  
  // Filter state management
  const [filters, setFiltersState] = useState<ServerFilterParams>(defaultFilters);

  // Debounce filters for the query key (prevents rapid API calls while typing)
  const debouncedFilters = useDebounce(filters, debounceDelay);

  // Create a stable query key from debounced filters
  const queryKey = useMemo(() => {
    const preparedFilters = prepareFiltersForRPC(debouncedFilters);
    return [rpcFunction, preparedFilters] as const;
  }, [rpcFunction, debouncedFilters]);

  // TanStack Query for data fetching with caching
  const {
    data: queryData,
    isLoading,
    isFetching,
    error: queryError,
    refetch: queryRefetch
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const preparedFilters = prepareFiltersForRPC(debouncedFilters);
      
      const { data: response, error: rpcError } = await supabase
        .rpc(rpcFunction, preparedFilters);

      if (rpcError) {
        throw new Error(`Failed to fetch data: ${rpcError.message}`);
      }

      if (response) {
        // Response is a single object with data arrays and metadata
        const result = response;
        
        // Handle projects, job_cards, invoices, and documents response formats
        const dataArray = result.job_cards || result.projects || result.invoices || result.documents || result.contacts || result.clients || result.ncrs || result.cors || [];
        const processedData = transformData && dataArray 
          ? transformData(dataArray) 
          : dataArray;

        return {
          data: processedData as T[],
          pagination: result.page_info || null,
          summary: result.summary || null
        };
      }

      return {
        data: [] as T[],
        pagination: null,
        summary: null
      };
    },
    // Keep previous data while fetching new data (smoother UX)
    placeholderData: (previousData) => previousData,
    refetchOnMount,
  });

  /**
   * Update filters and reset to first page if page-affecting filters changed
   */
  const setFilters = useCallback((newFilters: Partial<ServerFilterParams>) => {
    setFiltersState(prevFilters => {
      const updatedFilters = { ...prevFilters, ...newFilters };
      
      // Reset to first page if non-pagination filters changed,
      // but ONLY if page_offset wasn't explicitly provided in the update.
      // This allows URL-synced components to pass all filters including page_offset
      // without having it reset.
      const pageAffectingKeys = Object.keys(newFilters).filter(
        key => !['page_offset', 'page_size', 'sort_by', 'sort_order'].includes(key)
      );
      
      if (pageAffectingKeys.length > 0 && !('page_offset' in newFilters)) {
        updatedFilters.page_offset = 0;
      }
      
      return updatedFilters;
    });
  }, []);

  /**
   * Reset filters to default values
   */
  const resetFilters = useCallback(() => {
    setFiltersState(defaultFilters);
  }, [defaultFilters]);

  /**
   * Refetch data with current filters (invalidates cache)
   */
  const refetch = useCallback(async () => {
    // Invalidate the cache for this query to force a fresh fetch
    await queryClient.invalidateQueries({ queryKey: [rpcFunction] });
    await queryRefetch();
  }, [queryClient, rpcFunction, queryRefetch]);

  // Extract data from query result with defaults
  const data = queryData?.data ?? [];
  const pagination = queryData?.pagination ?? null;
  const summary = queryData?.summary ?? null;

  // Map TanStack Query states to our existing API
  // loading = true only on initial load (no cached data)
  // filtering = true when fetching with existing data
  const loading = isLoading;
  const filtering = isFetching && !isLoading;

  // Convert error to string for backwards compatibility
  const error = queryError 
    ? (queryError instanceof Error ? queryError.message : 'An unknown error occurred')
    : null;

  return {
    data,
    pagination,
    summary,
    loading,
    filtering,
    error,
    filters,
    setFilters,
    refetch,
    resetFilters
  };
};
